import type { LogsEvent, Logger } from '@datadog/browser-logs';
import type { RumEvent } from '@datadog/browser-rum';

const datadogLogger = require('@datadog/browser-logs') as typeof import('@datadog/browser-logs');
const datadogRum = require('@datadog/browser-rum') as typeof import('@datadog/browser-rum');
const configurationManager = require('../config/configurationManager') as typeof import('../config/configurationManager');
const stateManager = require('../state/stateManager') as typeof import('../state/stateManager');
const guidUtil = require('../guid/guidUtil') as typeof import('../guid/guidUtil');
const stringUtil = require('../string/stringUtil') as typeof import('../string/stringUtil');

type LogContext = object;

interface BackOfficeLoggerModule {
    initializeDatadog(): void;
    resetGlobalContext(this: BackOfficeLoggerModule): void;
    attachOrgToGlobalContext(this: BackOfficeLoggerModule): void;
    info(this: BackOfficeLoggerModule, message: string, messageContext?: LogContext, tag?: unknown): void;
    warn(this: BackOfficeLoggerModule, message: string, messageContext?: LogContext, error?: unknown, tag?: unknown): void;
    error(this: BackOfficeLoggerModule, message: string, messageContext?: LogContext, error?: unknown, tag?: unknown): void;
    metrics(metricName: string, metricValue: number, metricDescription: string, messageContext?: LogContext): void;
    usage(featureName: string, featureDescription: string, messageContext?: LogContext): void;
    setLoggedInUser(email: string, name: string): void;
    getEnvironment(): string;
    getService(): string;
}

let ddLogger: Logger | undefined;
const sessionId = guidUtil.generateGuid();

const backOfficeLogger = {} as BackOfficeLoggerModule;

backOfficeLogger.getEnvironment = (): string => {
    let env = configurationManager.getConfig().environment as string;
    if (stringUtil.areStringSame(env, 'ukwest')) env = 'production';
    return env;
};

backOfficeLogger.getService = (): string => 'RMS.APP.BACKOFFICE';

backOfficeLogger.initializeDatadog = (): void => {
    if (ddLogger != null) return;

    const config = configurationManager.getConfig();

    if (
        stringUtil.isStringNullOrEmpty(config.dataDogClientToken as string) ||
        stringUtil.isStringNullOrEmpty(config.dataDogSite as string)
    )
        return;

    datadogLogger.datadogLogs.init({
        clientToken: config.dataDogClientToken as string,
        site: config.dataDogSite as string,
        env: backOfficeLogger.getEnvironment(),
        service: backOfficeLogger.getService(),
        version: config.version as string,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        beforeSend: (log: LogsEvent) => {
            if (log?.error?.source === 'console') {
                return false;
            }
            return true;
        },
    });

    datadogRum.datadogRum.init({
        useCrossSiteSessionCookie: true,
        trackSessionAcrossSubdomains: true,
        applicationId: config.dataDogRumApplicationIdForBackOffice as string,
        clientToken: config.dataDogRumClientTokenForBackOffice as string,
        site: config.dataDogRumSiteForBackOffice as string,
        service: backOfficeLogger.getService(),
        env: backOfficeLogger.getEnvironment(),
        version: config.version as string,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        allowedTracingUrls: [/https:\/\/.*\.flipdish\.co/],
        beforeSend: (event: RumEvent) => {
            if (event.type === 'error' && event.error.source === 'console') {
                return false;
            }
            return true;
        },
    });

    datadogRum.datadogRum.setGlobalContextProperty('team', 'res-opt-team');

    ddLogger = datadogLogger.datadogLogs.logger;
};

backOfficeLogger.resetGlobalContext = function (this: BackOfficeLoggerModule): void {
    this.initializeDatadog();
    if (ddLogger == null) return;

    datadogLogger.datadogLogs.setGlobalContext({
        env: backOfficeLogger.getEnvironment(),
        shellType: 'Browser',
        renderingMode: 'Desktop',
        version: configurationManager.getConfig().version as string,
        sessionId: sessionId,
    });

    const globalState = stateManager.getStore().getState();
    if (globalState.applicationModeData != null && globalState.applicationModeData.rumSessionId != null) {
        datadogRum.datadogRum.setGlobalContextProperty('portal_session_id', globalState.applicationModeData.rumSessionId);
    }
};

backOfficeLogger.attachOrgToGlobalContext = function (this: BackOfficeLoggerModule): void {
    this.initializeDatadog();
};

backOfficeLogger.info = function (
    this: BackOfficeLoggerModule,
    message: string,
    messageContext: LogContext = {},
    tag?: unknown
): void {
    try {
        const resolvedContext = initializeMessageContext(messageContext, tag);
        console.info(message);
        if (ddLogger != null) ddLogger.info(message, resolvedContext);
    } catch (_error: unknown) {}
};

backOfficeLogger.warn = function (
    this: BackOfficeLoggerModule,
    message: string,
    messageContext: LogContext = {},
    error?: unknown,
    tag?: unknown
): void {
    try {
        const resolvedContext = initializeMessageContext(messageContext, tag);
        if (error != null) {
            resolvedContext.errorMessage = (error as Error).toString();
            (message as unknown as { errorStack?: string }).errorStack = (error as { stack?: string }).stack;
        }

        console.warn(message);
        if (ddLogger != null) ddLogger.warn(message, resolvedContext);
    } catch (_err: unknown) {}
};

backOfficeLogger.error = function (
    this: BackOfficeLoggerModule,
    message: string,
    messageContext?: LogContext,
    error?: unknown,
    tag?: unknown
): void {
    try {
        const resolvedContext = initializeMessageContext(messageContext, tag);

        if (ddLogger != null) ddLogger.error(message, resolvedContext, error as Error | undefined);
        else console.error(message);
    } catch (_err: unknown) {}
};

backOfficeLogger.metrics = (
    metricName: string,
    metricValue: number,
    metricDescription: string,
    messageContext?: LogContext
): void => {
    const resolvedContext = initializeMessageContext(messageContext);
    resolvedContext.loggingType = 'metrics';
    resolvedContext.metricName = metricName.toUpperCase();
    resolvedContext.metricValue = metricValue * 1;
    resolvedContext.metricDescription = metricDescription;

    const message = `Recording metric (${metricName})`;
    console.info(message);
    if (ddLogger != null) ddLogger.info(message, resolvedContext);
};

backOfficeLogger.usage = (
    featureName: string,
    featureDescription: string,
    messageContext?: LogContext
): void => {
    datadogRum.datadogRum.addAction(featureName);

    const resolvedContext = initializeMessageContext(messageContext);
    resolvedContext.loggingType = 'features';
    resolvedContext.featureName = featureName;
    resolvedContext.featureCount = 1;
    resolvedContext.featureDescription = featureDescription;

    const message = `Recording feature (${featureName})`;
    console.info(message);
    if (ddLogger != null) ddLogger.info(message, resolvedContext);
};

backOfficeLogger.setLoggedInUser = (email: string, name: string): void => {
    try {
        datadogRum.datadogRum.setGlobalContextProperty('name', name);
        datadogRum.datadogRum.setGlobalContextProperty('email', email);
        datadogRum.datadogRum.setUser({ email, name });
    } catch (_error: unknown) {}
};

function initializeMessageContext(messageContext: LogContext | null | undefined, tag?: unknown): Record<string, unknown> {
    if (messageContext == null) messageContext = {};
    const ctx = messageContext as Record<string, unknown>;

    ctx.loggingDateTime = Date.now();
    ctx.loggingType = 'logging';
    ctx.tag = tag;
    return ctx;
}

export default backOfficeLogger;
export const initializeDatadog = backOfficeLogger.initializeDatadog.bind(backOfficeLogger);
export const resetGlobalContext = backOfficeLogger.resetGlobalContext.bind(backOfficeLogger);
export const attachOrgToGlobalContext = backOfficeLogger.attachOrgToGlobalContext.bind(backOfficeLogger);
export const info = backOfficeLogger.info.bind(backOfficeLogger);
export const warn = backOfficeLogger.warn.bind(backOfficeLogger);
export const error = backOfficeLogger.error.bind(backOfficeLogger);
export const metrics = backOfficeLogger.metrics.bind(backOfficeLogger);
export const usage = backOfficeLogger.usage.bind(backOfficeLogger);
export const setLoggedInUser = backOfficeLogger.setLoggedInUser.bind(backOfficeLogger);
export const getEnvironment = backOfficeLogger.getEnvironment.bind(backOfficeLogger);
export const getService = backOfficeLogger.getService.bind(backOfficeLogger);
